import { useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";
import { api } from "@/lib/api";

/**
 * MatriculationCountdown — a slim ribbon on the Overview tab that
 * counts down to matriculation / freshers week. Reads the next
 * matriculation date from `/application-timeline` (kind == matriculate).
 *
 * If matriculation has already passed this cycle, we roll forward to
 * next year's date so the countdown is always positive and useful for
 * an incoming cohort planning the move.
 */
export default function MatriculationCountdown({ uniId }) {
  const [days, setDays] = useState(null);
  const [dateLabel, setDateLabel] = useState("");
  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uniId}/application-timeline`)
      .then((r) => {
        if (cancelled) return;
        const mat = (r.data?.stages || []).find((s) => s.kind === "matriculate");
        if (!mat) return;
        const now = new Date();
        // Try this year, roll forward if the date has passed.
        let target = new Date(now.getFullYear(), mat.month - 1, mat.day);
        if (target < now) {
          target = new Date(now.getFullYear() + 1, mat.month - 1, mat.day);
        }
        const diffMs = target - now;
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        setDays(diffDays);
        setDateLabel(target.toLocaleDateString("en-GB", { month: "long", day: "numeric", year: "numeric" }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uniId]);

  if (days == null) return null;

  return (
    <div
      className="flex items-center justify-between gap-4 p-4 md:p-5 mb-8 border-l-4 border-[#7C3AED] bg-gradient-to-r from-[#7C3AED]/[0.06] to-transparent"
      data-testid="matriculation-countdown"
    >
      <div className="flex items-center gap-3 min-w-0">
        <CalendarClock className="h-5 w-5 shrink-0 text-[#7C3AED]" />
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#7C3AED] mb-0.5">
            Matriculation countdown
          </div>
          <div className="font-serif text-base md:text-lg text-[#002147] leading-tight">
            Term begins <span className="font-semibold">{dateLabel}</span>
          </div>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-serif text-3xl md:text-4xl text-[#7C3AED] leading-none tabular-nums">
          {days}
        </div>
        <div className="font-mono text-[9px] uppercase tracking-widest text-[#002147]/60 mt-1">
          day{days === 1 ? "" : "s"} away
        </div>
      </div>
    </div>
  );
}
